import moment from 'moment';
import { useQuery } from 'react-query';
import { addDays, subDays } from 'date-fns';
import { downloadImage } from 'utils/download';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCurrentClientCurrency } from 'hooks/useCurrentClientCurrency';

import { DEFAULT_STALE_TIME } from '../../constants';

// COMPONENTS
import DetailsSection from './DetailsSection';
import { fetchClusterData } from './Cluster';
import LoadingSpinner from '../LoadingSpinner';
import FilterBarWrapper from '../FilterBarWrapper';
import DatepickerComponent from '../DatepickerComponent';
import TooltipPopover from 'components/TooltipPopover';

// TYPES
import { DateValueType } from 'react-tailwindcss-datepicker';

// UTILS
import { toFixedNum, formatCurrencySymbol } from 'utils';
import InsightsCustomSelector from './InsightsCustomSelector';

interface MetricCardProps {
  title: string;
  value: number | string;
  className: string;
}

interface APIResponse {
  recommendations: {
    [category: string]: Array<{
      cluster_category: string;
      recommendations: string;
      organic_impressions: number;
      organic_clicks: number;
      organic_transactions: number;
      organic_revenue: number;
      paid_impressions: number;
      paid_clicks: number;
      paid_cost: number;
      paid_transactions: number;
      paid_revenue: number;
      top_search_terms: string[];
    }>;
  };
}

export const fetchRecommendations = async (
  startDate: string,
  endDate: string
): Promise<APIResponse> => {
  const response = await fetch(
    `/get_insights_rec_data/${startDate}/${endDate}`
  );
  if (!response.ok) {
    const errorData = await response.json();
    if (errorData.error === "'json_data'") {
      throw new Error(
        'No data for this time period, please change the date range'
      );
    }
    throw new Error('Network response was not ok');
  }
  return response.json();
};

const formatCategory = (category: string): string => {
  // Check if the category is already in title case and contains no hyphens
  if (/^[A-Z][a-z]*$/.test(category)) {
    return category;
  }

  // Split the category by hyphen and capitalize each word
  return category
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// METRIC CARD COMPONENT
function MetricCard({ title, value, className }: MetricCardProps) {
  const formatTitle = (title: string) => {
    return title
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className={`p-6 ${className}`}>
      <TooltipPopover
        content={`insights_page.recommended_tab.recommendations_for_other.${title
          .replace(' ', '_')
          .toLowerCase()}`}
        type="dashed"
        side="top"
      >
        <h4 className="font-bold text-center">{formatTitle(title)}</h4>
      </TooltipPopover>
      <p className="text-center">
        {typeof value === 'number' ? toFixedNum(value) : value}
      </p>
    </div>
  );
}

// RECOMMENDATIONS PAGE
function Recommendations() {
  const [activeCategory, setActiveCategory] = useState('');
  const [activeRecommendationIndex, setActiveRecommendationIndex] =
    useState<number>(0);
  const [value, setValue] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: addDays(new Date(), -90), // 90 days before today
    endDate: subDays(new Date(), 3),
  });

  const { startDate, endDate } = value;
  const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
  const formattedEndDate = moment(endDate).format('YYYY-MM-DD');

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [searchParams] = useSearchParams();
  const usecases = searchParams.get('usecases');

  const { currencySymbol } = useCurrentClientCurrency();

  // Add space if the toLocalString() returns currency code instead of symbol, when symbol for that currency is not available (for readability)
  const currencySymbolWithSpace = formatCurrencySymbol(currencySymbol);

  // Check if there is data available for the date range
  const { data: overallData, isLoading: isOverallDataLoading } = useQuery(
    ['overallData', formattedStartDate, formattedEndDate],
    () => fetchClusterData(formattedStartDate, formattedEndDate),
    {
      enabled: !!startDate && !!endDate,
      staleTime: DEFAULT_STALE_TIME,
    }
  );

  // Get recommendations data
  const { data, isLoading, error } = useQuery(
    ['recommendations', formattedStartDate, formattedEndDate],
    () => fetchRecommendations(formattedStartDate, formattedEndDate),
    {
      enabled: !!startDate && !!endDate,
      staleTime: DEFAULT_STALE_TIME,
      onSuccess: (data) => {
        //active category and recommendation index states are set accordingly.
        if (!activeCategory && Object.keys(data.recommendations).length > 0) {
          const firstCategory = Object.keys(data.recommendations)[0];
          setActiveCategory(firstCategory);
          setActiveRecommendationIndex(0);
        }
      },
    }
  );

  const handleValueChange = (newValue: DateValueType) => {
    if (newValue?.startDate && newValue?.endDate) {
      setValue({
        startDate: new Date(newValue.startDate),
        endDate: new Date(newValue.endDate),
      });
    }
  };

  const handlePrevRecommendation = () => {
    if (activeRecommendationIndex > 0) {
      setActiveRecommendationIndex(activeRecommendationIndex - 1);
    }
  };

  const handleNextRecommendation = () => {
    if (
      activeRecommendationIndex <
      (data?.recommendations[activeCategory]?.length || 0) - 1
    ) {
      setActiveRecommendationIndex(activeRecommendationIndex + 1);
    }
  };

  const categories = useMemo(() => {
    if (!data?.recommendations) return [];

    let categoryArray = Object.keys(data.recommendations);

    // Sort recommendations by paid clicks
    categoryArray.forEach((category) => {
      data.recommendations[category].sort(
        (a, b) => (b.paid_clicks || 0) - (a.paid_clicks || 0) // Sort descending
      );
    });

    categoryArray = categoryArray
      .map((category) => ({
        category,
        maxPaidClicks: data.recommendations[category][0]?.paid_clicks || 0, // First element has max clicks
      }))
      .sort((a, b) => b.maxPaidClicks - a.maxPaidClicks) // Sort categories descending
      .map((item) => item.category);

    return categoryArray;
  }, [data?.recommendations, usecases]);

  useEffect(() => {
    if (categories.length > 0) {
      setActiveCategory(categories[0]);
      setActiveRecommendationIndex(0);
    }
  }, [categories, usecases]);

  const onChangeCategory = useCallback((category: string) => {
    setActiveCategory(category);
    setActiveRecommendationIndex(0);
  }, []);

  const activeRecommendations = data?.recommendations[activeCategory] || [];
  const activeRecommendation =
    activeRecommendations[activeRecommendationIndex] || {};
  const top_search_termsArray = activeRecommendation.top_search_terms || [];
  const uniquetop_search_terms = new Set(top_search_termsArray);
  const uniquetop_search_termsCount = uniquetop_search_terms.size;
  const metrics = {
    organic_impressions: activeRecommendation.organic_impressions || 0,
    paid_impressions: activeRecommendation.paid_impressions || 0,
    organic_clicks: activeRecommendation.organic_clicks || 0,
    paid_clicks: activeRecommendation.paid_clicks || 0,
    organic_transactions: activeRecommendation.organic_transactions || 0,
    paid_transactions: activeRecommendation.paid_transactions || 0,
    organic_revenue: activeRecommendation.organic_revenue || 0,
    paid_revenue: activeRecommendation.paid_revenue || 0,
    cost: activeRecommendation.paid_cost || 0,
  };

  const specialIndexes = [3, 4, 5];

  const togglemenuDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const downloadMetricsImage = () => {
    downloadImage('recommendations');
  };

  useEffect(() => {
    if (usecases === 'auto_optimize_performance') {
      const matchedCategory = categories.find(
        (category) =>
          category.includes('Auto Optimize') ||
          category.includes('Auto Optimise') ||
          category.includes('AutoOptimize') ||
          category.includes('AutoOptimise')
      );

      if (matchedCategory) {
        setActiveCategory(matchedCategory);
      }
    }
  }, [usecases, categories]);

  return (
    <div className="pt-8">
      <FilterBarWrapper>
        <div className="flex-1" />
        <TooltipPopover
          content="insights_page.date_range_filter"
          type="popup"
          side="top"
        >
          <DatepickerComponent
            value={value}
            onChange={handleValueChange}
            product="insights"
          />
        </TooltipPopover>
      </FilterBarWrapper>
      {/* Has overall data but no recommendations */}
      {!isLoading &&
      !isOverallDataLoading &&
      !categories.length &&
      overallData?.search_terms &&
      Object.keys(overallData.search_terms ?? {}).length > 0 ? (
        <div>
          <p>No Recommendations - Please try selecting different date range</p>
        </div>
      ) : null}

      {/* No data at all */}
      {!isLoading &&
      !isOverallDataLoading &&
      Object.keys(overallData?.search_terms ?? {}).length === 0 ? (
        <div>
          <p>No data available</p>
        </div>
      ) : null}

      {!isLoading && data && categories.length ? (
        <>
          <InsightsCustomSelector
            options={categories}
            onChange={onChangeCategory}
            value={activeCategory}
          />

          <div id="recommendations">
            <div className="flex justify-between items-start">
              <div className="text-left">
                <h1 className="text-2xl font-bold">
                  Recommendations for {formatCategory(activeCategory)}
                </h1>
                <h2 className="text-sm font-bold text-gray800 mb-4">
                  Top 4 recommendation based on your recommendation indicators
                </h2>
              </div>
              <div
                className="relative"
                ref={dropdownRef}
                id="downloadDropdownMenu-recommendations"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/static/img/dots-vertical.svg`}
                  alt="dots"
                  className="h-6 w-6 mr-4 cursor-pointer"
                  onClick={togglemenuDropdown}
                />
                {isDropdownOpen && (
                  <div
                    className="absolute right-4 mt-2 w-48 bg-light text-sm text-gray700 rounded-md z-50"
                    style={{ boxShadow: '1px 1px 8px 0px rgba(0, 0, 0, 0.15)' }}
                  >
                    <ul>
                      {/* <li className="px-4 py-2 hover:bg-gray-100 cursor-pointer" onClick={closeDropdown}>View data table</li> */}
                      {/* <li className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        closeDropdown();
                      }}
                  >
                      Download XLS
                  </li> */}
                      <li
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          downloadMetricsImage();
                          closeDropdown();
                        }}
                      >
                        Download PNG image
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-success mb-8"></div>
            <div className="flex flex-col lg:flex-row justify-between">
              <div className="flex flex-col w-full lg:w-1/2 mr-6">
                <div className="bg-secondarygray text-light p-4 rounded-2xl mb-8 flex-grow h-[148px] overflow-auto ">
                  <div className="flex justify-between items-center mb-2 border-b border-success">
                    <h3 className="font-bold text-base">Recommendation</h3>
                    <div className="flex items-center">
                      <img
                        src={`${process.env.PUBLIC_URL}/static/img/right-arrow.svg`}
                        alt="left icon"
                        className={`inline mr-2 rotate-180 cursor-pointer ${
                          activeRecommendationIndex === 0
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                        onClick={handlePrevRecommendation}
                      />
                      <span className="text-primarygray font-normal text-base mb-1 px-2 bg-light rounded-[100px]">
                        {activeRecommendationIndex + 1} of{' '}
                        {activeRecommendations.length}
                      </span>
                      <img
                        src={`${process.env.PUBLIC_URL}/static/img/right-arrow.svg`}
                        alt="right icon"
                        className={`inline ml-2 cursor-pointer ${
                          activeRecommendationIndex ===
                          activeRecommendations.length - 1
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                        onClick={handleNextRecommendation}
                      />
                    </div>
                  </div>
                  <div className="h-[calc(100%-46px)]">
                    <p>{activeRecommendation.recommendations}</p>
                  </div>
                </div>

                <div className="bg-light p-4 rounded-2xl flex-grow overflow-auto h-[148px]">
                  <div className="flex items-center mb-2 border-b border-success">
                    <span className="bg-fourthgray text-primarygray font-bold py-1 px-3 rounded-full mb-2 mr-2">
                      {uniquetop_search_termsCount}
                    </span>
                    <TooltipPopover
                      content="insights_page.recommended_tab.recommendations_for_other.search_terms"
                      type="dashed"
                      side="top"
                    >
                      <h4 className="text-primarygray text-base font-bold mb-2">
                        Search Terms
                      </h4>
                    </TooltipPopover>
                  </div>
                  <div className="h-[calc(100% - 46px)] overflow-auto">
                    <p>{top_search_termsArray.join(', ')}</p>
                  </div>
                </div>
              </div>

              <div className="w-full rounded-lg lg:w-1/2 mt-8 lg:mt-0">
                <div className="grid grid-cols-3 text-center text-sm">
                  {Object.keys(metrics).map((key, index) => {
                    const formattedKey = key
                      .replace(/_/g, ' ')
                      .split(' ')
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(' ');

                    const totalCards = Object.keys(metrics).length;
                    const cols = 3;
                    const rows = Math.ceil(totalCards / cols);
                    const currentRow = Math.floor(index / cols);
                    const isLastRow = currentRow === rows - 1;
                    const colIndex = index % cols;

                    let borderRadiusClasses = '';

                    if (index === 0) {
                      borderRadiusClasses = 'rounded-tl-2xl';
                    } else if (index === 2) {
                      borderRadiusClasses = 'rounded-tr-2xl';
                    }

                    if (isLastRow) {
                      const itemsInLastRow = totalCards % cols || cols;

                      if (itemsInLastRow === 1 && index === totalCards - 1) {
                        // Only one item in the last row
                        borderRadiusClasses = 'rounded-2xl';
                      } else if (itemsInLastRow === 2) {
                        if (colIndex === 0) {
                          borderRadiusClasses = 'rounded-bl-2xl';
                        } else if (colIndex === 1) {
                          borderRadiusClasses = 'rounded-br-2xl';
                        }
                      } else if (itemsInLastRow === 3) {
                        if (colIndex === 0) {
                          borderRadiusClasses = 'rounded-bl-2xl';
                        } else if (colIndex === 2) {
                          borderRadiusClasses = 'rounded-br-2xl';
                        }
                      }
                    }

                    return (
                      <MetricCard
                        key={index}
                        title={formattedKey}
                        value={
                          key.includes('revenue') || key.includes('cost')
                            ? `${currencySymbolWithSpace}${toFixedNum(
                                metrics[key as keyof typeof metrics]
                              )}`
                            : toFixedNum(metrics[key as keyof typeof metrics])
                        }
                        className={`${
                          specialIndexes.includes(index)
                            ? 'bg-fourthgray'
                            : 'bg-light'
                        } ${
                          index % 3 !== 0 ? 'border-l border-primarygray' : ''
                        } ${borderRadiusClasses}`}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <DetailsSection
            activeCategory={activeCategory}
            startDate={formattedStartDate}
            endDate={formattedEndDate}
          />
        </>
      ) : null}

      {isLoading ? <LoadingSpinner /> : null}
      {error ? <div>An error occurred while fetching data.</div> : null}
    </div>
  );
}

export default Recommendations;
